import React, { useState, useCallback } from 'react';
import {
  ANIMALS,
  SENSORY_DESCRIPTORS,
  SHAPES,
  LETTERS,
  formatPseudonym,
} from '@tl4k/frontend-shared';
import AvatarPreview, { renderBgShape } from './AvatarPreview';
import type { AvatarConfig } from './AvatarPreview';

// ============================================================
// PSEUDONYM DISPLAY — ANIMALE SUL COLORE NEL FORMA COLORE
// ============================================================
const PseudonymDisplay: React.FC<{
  animal?: string;
  animalColor?: string;
  shape?: string;
  shapeColor?: string;
  letter?: string;
  compact?: boolean;
}> = ({ animal, animalColor, shape, shapeColor, letter, compact = false }) => {
  const config: AvatarConfig = {
    animal: animal ?? '',
    animal_color: animalColor ?? '',
    shape: shape ?? '',
    shape_color: shapeColor ?? '',
    letter,
  };
  const text = formatPseudonym(config);
  if (!text) return null;

  const fontSize = compact ? 13 : 17;
  const padding = compact ? '8px 14px' : '14px 22px';

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #2D2B55 0%, #4A3F8A 100%)',
        borderRadius: compact ? 12 : 16,
        padding,
        textAlign: 'center',
        boxShadow: '0 4px 16px rgba(45,43,85,0.25)',
      }}
    >
      {!compact && (
        <div
          style={{
            fontSize: 10,
            color: 'rgba(255,255,255,0.5)',
            fontWeight: 700,
            letterSpacing: 2,
            marginBottom: 6,
            fontFamily: "'Nunito', sans-serif",
          }}
        >
          IL TUO PSEUDONIMO
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'baseline',
          gap: compact ? 4 : 6,
        }}
      >
        <span
          style={{
            fontSize,
            fontWeight: 800,
            color: '#fff',
            fontFamily: "'Nunito', sans-serif",
            letterSpacing: 0.5,
          }}
        >
          {text}
        </span>
      </div>
    </div>
  );
};

// ============================================================
// STEP INDICATOR
// ============================================================
const StepIndicator: React.FC<{ totalSteps: number; currentStep: number }> = ({
  totalSteps,
  currentStep,
}) => (
  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 24 }}>
    {Array.from({ length: totalSteps }).map((_, i) => {
      const isDone = i < currentStep;
      const isActive = i === currentStep;
      return (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              fontWeight: 800,
              fontFamily: "'Nunito', sans-serif",
              background: isDone ? '#28B463' : isActive ? '#6C5CE7' : '#EDEAF5',
              color: isDone || isActive ? '#fff' : '#AAA',
              transition: 'all 0.3s ease',
              boxShadow: isActive ? '0 0 0 4px rgba(108,92,231,0.2)' : 'none',
            }}
          >
            {isDone ? '✓' : i + 1}
          </div>
          {i < totalSteps - 1 && (
            <div
              style={{
                width: 20,
                height: 3,
                borderRadius: 2,
                background: isDone ? '#28B463' : '#EDEAF5',
                transition: 'background 0.3s ease',
              }}
            />
          )}
        </div>
      );
    })}
  </div>
);

// ============================================================
// SELECTOR GRID
// ============================================================
const SelectorGrid: React.FC<{
  items: { id: string; label: string; emoji?: string; hex?: string }[];
  selected: string | null;
  onSelect: (id: string) => void;
  columns?: number;
  renderItem?: (item: { id: string; label: string; emoji?: string; hex?: string }, isSelected: boolean) => React.ReactNode;
}> = ({ items, selected, onSelect, columns = 4, renderItem }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: 10,
      maxWidth: 500,
      margin: '0 auto',
    }}
  >
    {items.map((item) => {
      const isSelected = selected === item.id;
      return (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect(item.id)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            padding: '12px 6px',
            borderRadius: 14,
            border: isSelected ? '3px solid #6C5CE7' : '3px solid transparent',
            background: isSelected ? 'rgba(108,92,231,0.08)' : '#F8F7FC',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            transform: isSelected ? 'scale(1.06)' : 'scale(1)',
            boxShadow: isSelected
              ? '0 4px 16px rgba(108,92,231,0.2)'
              : '0 1px 3px rgba(0,0,0,0.04)',
            outline: 'none',
          }}
          onMouseEnter={(e) => {
            if (!isSelected) e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = isSelected ? 'scale(1.06)' : 'scale(1)';
          }}
        >
          {renderItem ? renderItem(item, isSelected) : (
            <>
              <span style={{ fontSize: 32, lineHeight: 1.1 }}>{item.emoji}</span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: isSelected ? '#6C5CE7' : '#777',
                  fontFamily: "'Nunito', sans-serif",
                }}
              >
                {item.label}
              </span>
            </>
          )}
        </button>
      );
    })}
  </div>
);

const colorItemRenderer = (
  item: { id: string; label: string; hex?: string },
  isSelected: boolean
) => (
  <>
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        background: item.hex ?? '#ccc',
        border: `3px solid ${isSelected ? '#2D2B55' : 'white'}`,
        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
      }}
    />
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: isSelected ? '#6C5CE7' : '#777',
        fontFamily: "'Nunito', sans-serif",
      }}
    >
      {item.label}
    </span>
  </>
);

const shapeItemRenderer = (
  item: { id: string; label: string },
  isSelected: boolean
) => {
  const s = 40;
  const col = isSelected ? '#6C5CE7' : '#C4BFD9';
  return (
    <>
      <svg viewBox={`0 0 ${s} ${s}`} width={s} height={s}>
        {renderBgShape(item.id, col, s)}
      </svg>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: isSelected ? '#6C5CE7' : '#777',
          fontFamily: "'Nunito', sans-serif",
        }}
      >
        {item.label}
      </span>
    </>
  );
};

// ============================================================
// LETTER SELECTOR
// ============================================================
const LetterSelector: React.FC<{
  selected: string | null;
  onSelect: (letter: string) => void;
}> = ({ selected, onSelect }) => (
  <div style={{ textAlign: 'center' }}>
    <p
      style={{
        fontSize: 13,
        color: '#888',
        marginBottom: 12,
        fontFamily: "'Nunito', sans-serif",
        fontWeight: 700,
      }}
    >
      SCEGLI UNA LETTERA PER RENDERE UNICO IL TUO NOME
    </p>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(9, 1fr)',
        gap: 6,
        maxWidth: 400,
        margin: '0 auto',
      }}
    >
      {LETTERS.map((l) => {
        const isSel = selected === l;
        return (
          <button
            key={l}
            type="button"
            onClick={() => onSelect(l)}
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              border: isSel ? '3px solid #6C5CE7' : '3px solid transparent',
              background: isSel ? '#6C5CE7' : '#F0EEF8',
              color: isSel ? '#fff' : '#555',
              fontSize: 15,
              fontWeight: 800,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              fontFamily: "'Nunito', sans-serif",
              outline: 'none',
            }}
          >
            {l}
          </button>
        );
      })}
    </div>
  </div>
);

// ============================================================
// STEPS CONFIG
// ============================================================
const STEPS = [
  { key: 'animal', title: 'SCEGLI IL TUO ANIMALE', subtitle: 'QUALE ANIMALE TI RAPPRESENTA?' },
  { key: 'animalColor', title: 'SU QUALE DESCRITTORE?', subtitle: 'COLORE, TATTO O GUSTO — COME LO SENTI?' },
  { key: 'shape', title: 'SCEGLI UNA FORMA', subtitle: 'LA FORMA DELLO SFONDO' },
  { key: 'shapeColor', title: 'DI CHE DESCRITTORE?', subtitle: 'COLORE, TATTO O GUSTO — COME LO SENTI?' },
  { key: 'review', title: 'ECCO IL TUO PROFILO!', subtitle: 'CONTROLLA E CONFERMA' },
];

// ============================================================
// STYLES
// ============================================================
const S = {
  container: {
    minHeight: '100%',
    background: 'linear-gradient(160deg, #F0ECF9 0%, #E8E3F3 40%, #DDD8EE 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    fontFamily: "'Nunito', sans-serif",
  },
  card: {
    background: '#fff',
    borderRadius: 28,
    padding: '32px 24px',
    maxWidth: 560,
    width: '100%',
    boxShadow: '0 8px 40px rgba(108,92,231,0.10), 0 2px 8px rgba(0,0,0,0.04)',
  },
  title: {
    fontSize: 19,
    fontWeight: 900,
    color: '#2D2B55',
    margin: '0 0 4px',
    fontFamily: "'Nunito', sans-serif",
    letterSpacing: 0.3,
  },
  subtitle: {
    fontSize: 13,
    color: '#999',
    margin: 0,
    fontWeight: 700,
    fontFamily: "'Nunito', sans-serif",
    letterSpacing: 0.5,
  },
  btnPrimary: {
    padding: '12px 28px',
    borderRadius: 14,
    border: 'none',
    background: 'linear-gradient(135deg, #6C5CE7, #A29BFE)',
    color: '#fff',
    fontSize: 15,
    fontWeight: 800,
    cursor: 'pointer',
    fontFamily: "'Nunito', sans-serif",
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 14px rgba(108,92,231,0.3)',
    letterSpacing: 0.5,
  },
  btnSecondary: {
    padding: '10px 20px',
    borderRadius: 12,
    border: 'none',
    background: '#F0EEF8',
    color: '#6C5CE7',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
    fontFamily: "'Nunito', sans-serif",
    transition: 'all 0.2s ease',
    letterSpacing: 0.5,
  },
  btnOutline: {
    padding: '12px 24px',
    borderRadius: 14,
    border: '2px solid #E8E5F0',
    background: 'transparent',
    color: '#777',
    fontSize: 14,
    fontWeight: 800,
    cursor: 'pointer',
    fontFamily: "'Nunito', sans-serif",
    transition: 'all 0.2s ease',
    letterSpacing: 0.5,
  },
  statusBox: {
    padding: '12px 18px',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 800,
    fontFamily: "'Nunito', sans-serif",
    background: '#F0EEF8',
    color: '#6C5CE7',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    letterSpacing: 0.5,
  },
  spinner: {
    display: 'inline-block',
    width: 16,
    height: 16,
    border: '3px solid #E8E5F0',
    borderTopColor: '#6C5CE7',
    borderRadius: '50%',
    animation: 'spin 0.6s linear infinite',
  },
};

// ============================================================
// MAIN COMPONENT
// ============================================================
export type { AvatarConfig } from './AvatarPreview';

interface PseudonymBuilderProps {
  classId: string;
  pin: string;
  onCheckAvailability: (config: AvatarConfig) => Promise<boolean>;
  onSubmit: (config: AvatarConfig) => void;
  isSubmitting?: boolean;
}

const PseudonymBuilder: React.FC<PseudonymBuilderProps> = ({
  classId: _classId,
  pin: _pin,
  onCheckAvailability,
  onSubmit,
  isSubmitting = false,
}) => {
  const [step, setStep] = useState(0);
  const [animal, setAnimal] = useState<string | null>(null);
  const [animalColor, setAnimalColor] = useState<string | null>(null);
  const [shape, setShape] = useState<string | null>(null);
  const [shapeColor, setShapeColor] = useState<string | null>(null);
  const [letter, setLetter] = useState<string | null>(null);
  const [needsLetter, setNeedsLetter] = useState(false);
  const [checking, setChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const checkAvailability = useCallback(
    async (a: string | null, ac: string | null, sh: string | null, sc: string | null, l: string | null) => {
      setChecking(true);
      setIsAvailable(null);
      const config: AvatarConfig = {
        animal: a ?? '',
        animal_color: ac ?? '',
        shape: sh ?? '',
        shape_color: sc ?? '',
        letter: l ?? undefined,
      };
      const available = await onCheckAvailability(config);
      setChecking(false);
      setIsAvailable(available);
      if (!available && !l) setNeedsLetter(true);
      return available;
    },
    [onCheckAvailability]
  );

  const handleSelect = (value: string) => {
    if (step === 0) setAnimal(value);
    else if (step === 1) setAnimalColor(value);
    else if (step === 2) setShape(value);
    else if (step === 3) setShapeColor(value);
  };

  const canProceed = () => {
    if (step === 0) return !!animal;
    if (step === 1) return !!animalColor;
    if (step === 2) return !!shape;
    if (step === 3) return !!shapeColor;
    if (step === 4) return isAvailable === true;
    return false;
  };

  const goNext = async () => {
    if (step < 4) {
      const next = step + 1;
      setStep(next);
      if (next === 4) {
        await checkAvailability(animal, animalColor, shape, shapeColor, letter);
      }
    }
  };

  const goBack = () => {
    if (step > 0) {
      setStep(step - 1);
      setIsAvailable(null);
      setNeedsLetter(false);
      setLetter(null);
    }
  };

  const handleLetterSelect = async (l: string) => {
    setLetter(l);
    await checkAvailability(animal, animalColor, shape, shapeColor, l);
  };

  const handleConfirm = () => {
    const config: AvatarConfig = {
      animal: animal ?? '',
      animal_color: animalColor ?? '',
      shape: shape ?? '',
      shape_color: shapeColor ?? '',
      letter: letter ?? undefined,
    };
    onSubmit(config);
    setConfirmed(true);
  };

  const handleReset = () => {
    setStep(0);
    setAnimal(null);
    setAnimalColor(null);
    setShape(null);
    setShapeColor(null);
    setLetter(null);
    setNeedsLetter(false);
    setChecking(false);
    setIsAvailable(null);
    setConfirmed(false);
  };

  const buildSpokenLabel = () => {
    const config: AvatarConfig = {
      animal: animal ?? '',
      animal_color: animalColor ?? '',
      shape: shape ?? '',
      shape_color: shapeColor ?? '',
      letter: letter ?? undefined,
    };
    return formatPseudonym(config);
  };

  const speakPseudonym = () => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      const utt = new SpeechSynthesisUtterance(buildSpokenLabel());
      utt.lang = 'it-IT';
      utt.rate = 0.8;
      speechSynthesis.speak(utt);
    }
  };

  // `step` e' un indice: se lo store si desincronizza da STEPS, `STEPS[step]`
  // non esiste. Senza fallback il wizard andava in crash con undefined.
  const currentStepData = STEPS[step] ?? STEPS[0]!;

  // ---- CONFIRMED ----
  if (confirmed) {
    return (
      <div style={S.container}>
        <link
          href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap"
          rel="stylesheet"
        />
        <div style={S.card}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 52, marginBottom: 8 }}>🎉</div>
            <h2 style={{ ...S.title, fontSize: 24 }}>BENVENUTO!</h2>
            <p style={{ ...S.subtitle, marginBottom: 24 }}>IL TUO PROFILO È STATO CREATO</p>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <AvatarPreview
                animal={animal ?? undefined}
                animalColor={animalColor ?? undefined}
                shape={shape ?? undefined}
                shapeColor={shapeColor ?? undefined}
                size={200}
              />
            </div>
            <PseudonymDisplay
              animal={animal ?? undefined}
              animalColor={animalColor ?? undefined}
              shape={shape ?? undefined}
              shapeColor={shapeColor ?? undefined}
              letter={letter ?? undefined}
            />
            <button type="button" onClick={speakPseudonym} style={{ ...S.btnSecondary, marginTop: 16 }}>
              🔊 ASCOLTA IL TUO NOME
            </button>
            <div style={{ marginTop: 20 }}>
              <button type="button" onClick={handleReset} style={S.btnOutline}>
                RICOMINCIA
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- BUILDER ----
  return (
    <div style={S.container}>
      <link
        href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap"
        rel="stylesheet"
      />
      <div style={S.card}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 900,
              color: '#2D2B55',
              margin: 0,
              fontFamily: "'Nunito', sans-serif",
              letterSpacing: 0.5,
            }}
          >
            CREA IL TUO PSEUDONIMO
          </h1>
        </div>

        <StepIndicator totalSteps={STEPS.length} currentStep={step} />

        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <h2 style={S.title}>{currentStepData.title}</h2>
          <p style={S.subtitle}>{currentStepData.subtitle}</p>
        </div>

        {/* Live preview */}
        {(animal || animalColor || shape || shapeColor) && step < 4 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 14,
              marginBottom: 20,
              padding: '12px 16px',
              background: '#F8F7FC',
              borderRadius: 14,
              flexWrap: 'wrap',
            }}
          >
            <AvatarPreview
              animal={animal ?? undefined}
              animalColor={animalColor ?? undefined}
              shape={shape ?? undefined}
              shapeColor={shapeColor ?? undefined}
              size={64}
            />
            <div style={{ flex: 1, minWidth: 180 }}>
              <PseudonymDisplay
                animal={animal ?? undefined}
                animalColor={animalColor ?? undefined}
                shape={shape ?? undefined}
                shapeColor={shapeColor ?? undefined}
                letter={letter ?? undefined}
                compact
              />
            </div>
          </div>
        )}

        {/* Step content */}
        {step === 0 && (
          <SelectorGrid items={ANIMALS} selected={animal} onSelect={handleSelect} columns={4} />
        )}
        {step === 1 && (
          <SelectorGrid
            items={SENSORY_DESCRIPTORS}
            selected={animalColor}
            onSelect={handleSelect}
            columns={4}
            renderItem={colorItemRenderer}
          />
        )}
        {step === 2 && (
          <SelectorGrid
            items={SHAPES}
            selected={shape}
            onSelect={handleSelect}
            columns={3}
            renderItem={shapeItemRenderer}
          />
        )}
        {step === 3 && (
          <SelectorGrid
            items={SENSORY_DESCRIPTORS}
            selected={shapeColor}
            onSelect={handleSelect}
            columns={4}
            renderItem={colorItemRenderer}
          />
        )}

        {/* REVIEW */}
        {step === 4 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <AvatarPreview
                animal={animal ?? undefined}
                animalColor={animalColor ?? undefined}
                shape={shape ?? undefined}
                shapeColor={shapeColor ?? undefined}
                size={200}
              />
            </div>
            <PseudonymDisplay
              animal={animal ?? undefined}
              animalColor={animalColor ?? undefined}
              shape={shape ?? undefined}
              shapeColor={shapeColor ?? undefined}
              letter={letter ?? undefined}
            />
            <button type="button" onClick={speakPseudonym} style={{ ...S.btnSecondary, marginTop: 14 }}>
              🔊 ASCOLTA IL TUO PSEUDONIMO
            </button>

            <div style={{ marginTop: 20 }}>
              {checking && (
                <div style={S.statusBox}>
                  <span style={S.spinner} /> CONTROLLO DISPONIBILITÀ...
                </div>
              )}
              {!checking && isAvailable === true && !needsLetter && (
                <div style={{ ...S.statusBox, background: '#E8F8F0', color: '#27AE60' }}>
                  ✅ PSEUDONIMO DISPONIBILE!
                </div>
              )}
              {!checking && isAvailable === true && needsLetter && letter && (
                <div
                  style={{
                    ...S.statusBox,
                    background: '#E8F8F0',
                    color: '#27AE60',
                    marginBottom: 16,
                  }}
                >
                  ✅ PSEUDONIMO DISPONIBILE!
                </div>
              )}
              {!checking && isAvailable === false && (
                <div style={{ marginTop: 4 }}>
                  <div
                    style={{
                      ...S.statusBox,
                      background: '#FFF3E0',
                      color: '#E67E22',
                      marginBottom: 16,
                    }}
                  >
                    ⚠️ QUESTO NOME È GIÀ PRESO! SCEGLI UNA LETTERA
                  </div>
                </div>
              )}
              {needsLetter && (
                <div style={{ marginTop: 16 }}>
                  <LetterSelector selected={letter} onSelect={handleLetterSelect} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div
          style={{
            display: 'flex',
            justifyContent: step === 0 ? 'flex-end' : 'space-between',
            marginTop: 28,
            gap: 12,
          }}
        >
          {step > 0 && (
            <button type="button" onClick={goBack} style={S.btnOutline}>
              ← INDIETRO
            </button>
          )}
          {step < 4 && (
            <button
              type="button"
              onClick={goNext}
              disabled={!canProceed()}
              style={{
                ...S.btnPrimary,
                opacity: canProceed() ? 1 : 0.4,
                cursor: canProceed() ? 'pointer' : 'not-allowed',
              }}
            >
              AVANTI →
            </button>
          )}
          {step === 4 && isAvailable && (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isSubmitting}
              style={{
                ...S.btnPrimary,
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              {isSubmitting ? '...' : '✔ CONFERMA'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Inject spin keyframes
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(styleEl);
}

export default PseudonymBuilder;
